import React, { useState, useMemo } from "react";
import {
  Search,
  Filter,
  RefreshCw,
  Edit,
  Clock,
  Sparkles,
  Layers,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  RotateCcw,
  Copy,
  Check,
  Tag,
  User,
  Calendar,
  Barcode,
  X,
  Boxes,
  Package,
  DollarSign,
  AlertTriangle,
  ShieldCheck,
  FileText,
  Info,
  Sliders,
  Zap,
  Trash2,
} from "lucide-react";
import * as XLSX from "xlsx";

import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { getPriceManagementSkus, getSkuRawSnapshot, syncRecipeToErpag, deleteRecipeSku } from "../api/sku";

export default function RecipeHistoryTab({ onSelectRecipe }) {
  const { user, canRead, getAccess } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";

  const historyAccess = getAccess ? getAccess("CostingRecipeHistory") : (canRead("CostingRecipeHistory") ? "read" : "no_access");
  const detailedAccess = getAccess ? getAccess("CostingDetailedCosts") : (canRead("CostingDetailedCosts") ? "read" : "no_access");

  const canSeeRecipeHistory =
    historyAccess === "no_access"
      ? false
      : historyAccess === "read" || historyAccess === "write"
      ? true
      : isAdmin;

  const canSeeDetailedCosts =
    detailedAccess === "no_access"
      ? false
      : detailedAccess === "read" || detailedAccess === "write"
      ? true
      : isAdmin;

  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingSku, setSyncingSku] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortField, setSortField] = useState("createdAt"); // 'createdAt', 'sku', 'title'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc', 'desc'

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder(field === "createdAt" ? "desc" : "asc");
    }
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setUserFilter("all");
    setDateFilter("");
    setSortField("createdAt");
    setSortOrder("desc");
    setCurrentPage(1);
  };

  // Full Details Modal States
  const [detailModalItem, setDetailModalItem] = useState(null);
  const [detailModalSnapshot, setDetailModalSnapshot] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState("ingredients");
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Date formatting helper
  const formatDateTime = (dateVal) => {
    if (!dateVal) return "-";
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return String(dateVal);
      return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return String(dateVal);
    }
  };

  const handleDeleteRecipe = async (item) => {
    if (!item?.sku) return;
    setIsDeleting(true);
    try {
      const res = await deleteRecipeSku(item.sku);
      toast.success(res?.message || `SKU ${item.sku} deleted successfully from all collections!`, { duration: 5000 });
      setDeletingItem(null);
      fetchRealHistory();
    } catch (err) {
      console.error("Delete recipe error:", err);
      toast.error(err?.message || "Failed to delete SKU");
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch real past 90 days recipes from database
    const handleSyncToErpag = async (sku) => {
    if (!sku) return;
    setSyncingSku(sku);
    const loadingToast = toast.loading(`Syncing SKU ${sku} to ERPAG...`);
    try {
      const snapProd = detailModalSnapshot?.product || {};
      const snapPkg = detailModalSnapshot?.addedPackaging || {};
      const extraFields = {
        nameEn: snapProd.nameEn || "",
        nameFr: snapProd.nameFr || "",
        description: snapProd.descriptionEn || snapProd.description || "",
        fr: snapProd.fr || "",
        harmonizedTariff: snapProd.harmonizedTariff || "",
        lengthInch: snapProd.lengthInch || "",
        widthInch: snapProd.widthInch || "",
        heightInch: snapProd.heightInch || "",
        lengthMm: snapProd.lengthMm || "",
        widthMm: snapProd.widthMm || "",
        heightMm: snapProd.heightMm || "",
        ti: snapPkg.ti || snapProd.ti || "",
        hi: snapPkg.hi || snapProd.hi || "",
        ranking: snapProd.ranking || "",
        trademark: snapProd.trademark || "",
      };

      const res = await syncRecipeToErpag({ sku, extraFields });
      toast.dismiss(loadingToast);
      if (res?.success) {
        toast.success(`🎉 SKU ${sku} successfully synced to ERPAG!`, { duration: 6000 });
      } else if (res?.rateLimited) {
        toast.error(res.message || "ERPAG rate limit reached. Please wait 1 minute before retrying.", { duration: 7000 });
      } else {
        toast.error(res?.message || "ERPAG sync failed. You can use Excel Export as fallback.", { duration: 7000 });
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.message || "Failed to sync to ERPAG", { duration: 7000 });
    } finally {
      setSyncingSku(null);
    }
  };

const fetchRealHistory = async () => {
    try {
      setLoading(true);
      const res = await getPriceManagementSkus({ filter: "new", limit: 100 });
      const rawSkus = Array.isArray(res?.data) ? res.data : [];

      const formatted = rawSkus.map((item) => {
        const isCustom = String(item.category || "").toLowerCase().includes("custom") || String(item.sku || "").startsWith("15");
        const dateStr = item.createdAt ? new Date(item.createdAt).toISOString().split("T")[0] : "-";
        return {
          ...item,
          id: item._id || item.sku,
          sku: item.sku,
          title: item.customFields?.nameEn || item.name || item.sku,
          category: item.category || (isCustom ? "Spice Blends Custom VRAC" : "Spice Blends VRAC"),
          upc: item.eanUpc || item.barcode || item.upc || item.customFields?.upc || item.customFields?.barcode || "-",
          trademark: item.trademark || item.customFields?.trademark || "A Spice Affair",
          dateCreated: dateStr,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          user: item.createdBy || item.customFields?.createdBy || item.customerName || "Admin",
          type: isCustom ? "Custom" : "Regular",
          baseCost: item.cost || item.unitCost || item.pmCostPerKgCad || 0,
          totalCost: (item.cost || item.unitCost || item.pmCostPerKgCad || 0) + (isCustom ? 1.25 : 0.75),
          rawItem: item,
        };
      });

      setHistoryList(formatted);
    } catch (err) {
      console.error("Failed to load real recipe history:", err);
      setHistoryList([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (canSeeRecipeHistory) {
      fetchRealHistory();
    }
  }, [canSeeRecipeHistory]);

  // Modifying Modal State (Fully responsive Frontend preview)
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    sku: "",
    title: "",
    category: "Spice Blends VRAC",
    trademark: "A Spice Affair",
    prefix: "",
    type: "Regular",
    upc: "",
  });

  const categories = useMemo(() => {
    const set = new Set(historyList.map((item) => item.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [historyList]);

  const users = useMemo(() => {
    const set = new Set(historyList.map((item) => item.user).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [historyList]);

  // Filtering & Sorting
  const sortedList = useMemo(() => {
    const list = historyList.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.upc.includes(searchTerm) ||
        item.trademark.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        categoryFilter === "all" || item.category === categoryFilter;

      const matchesUser = userFilter === "all" || item.user === userFilter;

      const matchesDate =
        !dateFilter ||
        dateFilter === "all" ||
        item.dateCreated === dateFilter ||
        (item.createdAt && new Date(item.createdAt).toISOString().split("T")[0] === dateFilter);

      return matchesSearch && matchesCategory && matchesUser && matchesDate;
    });

    return list.sort((a, b) => {
      if (sortField === "sku") {
        return sortOrder === "asc"
          ? String(a.sku || "").localeCompare(String(b.sku || ""), undefined, { numeric: true })
          : String(b.sku || "").localeCompare(String(a.sku || ""), undefined, { numeric: true });
      }
      if (sortField === "title") {
        return sortOrder === "asc"
          ? String(a.title || "").localeCompare(String(b.title || ""), undefined, { sensitivity: "base" })
          : String(b.title || "").localeCompare(String(a.title || ""), undefined, { sensitivity: "base" });
      }
      // Default: createdAt (newest created on top)
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [historyList, searchTerm, categoryFilter, userFilter, dateFilter, sortField, sortOrder]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedList.length / itemsPerPage));
  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedList.slice(start, start + itemsPerPage);
  }, [sortedList, currentPage, itemsPerPage]);

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleExportExcel = () => {
    const exportData = sortedList.map((row) => ({
      SKU: row.sku,
      "TITLE / NAME": row.title,
      CATEGORY: row.category,
      UPC: row.upc,
      TRADEMARK: row.trademark,
      "DATE CREATED": row.dateCreated,
      USER: row.user,
      TYPE: row.type,
      "BASE COST": canSeeDetailedCosts ? row.baseCost : "Confidential",
      "TOTAL RM COST": canSeeDetailedCosts ? row.totalCost : "Confidential",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Recipe History (90 Days)");
    XLSX.writeFile(
      workbook,
      `Recipe_History_90Days_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const openDetailModal = async (item) => {
    if (!item) return;
    setDetailModalItem(item);
    setDetailModalSnapshot(null);
    setDetailLoading(true);
    setActiveDetailTab("ingredients");
    try {
      const res = await getSkuRawSnapshot(item.sku);
      if (res?.data) {
        setDetailModalSnapshot(res.data);
      }
    } catch (err) {
      console.error("Failed to load full details for SKU:", item.sku, err);
    } finally {
      setDetailLoading(false);
    }
  };

  const openRemodifyModal = (item) => {
    setSelectedItemForEdit(item);
    setEditForm({
      sku: item.sku,
      title: item.title,
      category: item.category,
      trademark: item.trademark,
      prefix: item.sku.substring(0, 4),
      type: item.type,
      upc: item.upc,
    });
  };

  const handleSimulateRegenerate = (e) => {
    e.preventDefault();
    // Simulate updating in state
    setHistoryList((prev) =>
      prev.map((it) => {
        if (it.id === selectedItemForEdit.id) {
          const isCustom = editForm.type === "Custom";
          const newCategory = isCustom
            ? "Spice Blends Custom VRAC"
            : "Spice Blends VRAC";
          const newPrefix = editForm.prefix || (isCustom ? "15" : "10");
          const newSku = `${newPrefix}${it.sku.slice(newPrefix.length)}`;
          return {
            ...it,
            sku: newSku,
            title: editForm.title.toUpperCase(),
            category: newCategory,
            trademark: editForm.trademark,
            type: editForm.type,
          };
        }
        return it;
      })
    );
    setSelectedItemForEdit(null);
  };

  if (!canSeeRecipeHistory) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
        <ShieldCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Recipe history and past formulations are confidential and restricted for your account. Please contact your administrator if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0 w-full max-w-full">
      {/* Top Card Banner */}
      <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 min-w-0 w-full max-w-full overflow-hidden">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-black text-white flex items-center justify-center font-bold">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Recipe Creation History (90 Days)
              </h2>
              {/* <p className="text-xs text-gray-500">
                Historical record of recipes created in the past 90 days. Click any recipe row or "Edit Recipe" to open the full Recipe Builder pre-filled with ingredients, packaging, and costs, allowing full editing with reserved UPC preservation.
              </p> */}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={fetchRealHistory}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 rounded-xl bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 transition shadow-xs disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-600 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleResetFilters}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 rounded-xl bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 transition shadow-xs hover:text-black cursor-pointer"
            title="Reset all search, category, user, date filters and sorting to default"
          >
            <RotateCcw className="w-3.5 h-3.5 text-gray-600" />
            Reset Filter
          </button>
          <button
            onClick={handleExportExcel}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 rounded-xl bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 transition shadow-xs cursor-pointer"
          >
            <Download className="w-3.5 h-3.5 text-gray-600" />
            Export (Excel)
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-4 shadow-sm min-w-0 w-full max-w-full overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search SKU, Title, UPC, Trademark..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="all">All Categories</option>
              {categories
                .filter((c) => c !== "all")
                .map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
            </select>
          </div>

          {/* User Filter */}
          <div>
            <select
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="all">All Users</option>
              {users
                .filter((u) => u !== "all")
                .map((u) => (
                  <option key={u} value={u}>
                    User: {u}
                  </option>
                ))}
            </select>
          </div>

          {/* Select by Date Filter */}
          <div className="relative">
            <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-8 py-2 text-xs border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-black cursor-pointer text-gray-700"
              title="Select by Date to filter recipes"
            />
            {dateFilter && (
              <button
                type="button"
                onClick={() => {
                  setDateFilter("");
                  setCurrentPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black p-0.5 rounded-full hover:bg-gray-100 transition cursor-pointer"
                title="Clear Date Filter"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Quick Counter & Clear Filters */}
          <div className="flex items-center justify-between sm:justify-end gap-2 px-3 py-2 bg-gray-50 rounded-xl text-xs text-gray-600 font-medium border border-gray-200">
            <span>Showing:</span>
            <span className="font-bold text-gray-900 font-mono">
              {sortedList.length} of {historyList.length}
            </span>
            {(searchTerm || categoryFilter !== "all" || userFilter !== "all" || dateFilter || sortField !== "createdAt" || sortOrder !== "desc") && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="ml-1 p-1 hover:bg-gray-200 rounded-md text-gray-500 hover:text-black transition cursor-pointer"
                title="Reset all filters & sorting to default"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden min-w-0 w-full max-w-full">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm divide-y divide-gray-200">
            <thead className="bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider select-none">
              <tr>
                {/* SKU with sort arrow */}
                <th
                  onClick={() => handleSort("sku")}
                  className="px-5 py-3.5 whitespace-nowrap cursor-pointer hover:bg-gray-100 hover:text-black transition"
                  title="Click to sort by SKU"
                >
                  <div className="flex items-center gap-1.5">
                    <span>SKU</span>
                    {sortField === "sku" ? (
                      sortOrder === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5 text-blue-600" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                    )}
                  </div>
                </th>

                {/* Title / Description with sort arrow */}
                <th
                  onClick={() => handleSort("title")}
                  className="px-5 py-3.5 whitespace-nowrap cursor-pointer hover:bg-gray-100 hover:text-black transition"
                  title="Click to sort by Name / Title"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Title / Description</span>
                    {sortField === "title" ? (
                      sortOrder === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5 text-blue-600" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                    )}
                  </div>
                </th>

                <th className="px-5 py-3.5 whitespace-nowrap">Category</th>
                <th className="px-5 py-3.5 whitespace-nowrap">UPC (Reserved)</th>
                <th className="px-5 py-3.5 whitespace-nowrap">Trademark</th>

                {/* Date Created with sort arrow */}
                <th
                  onClick={() => handleSort("createdAt")}
                  className="px-5 py-3.5 whitespace-nowrap cursor-pointer hover:bg-gray-100 hover:text-black transition"
                  title="Click to sort by Date Created (Default: newest on top)"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Date Created</span>
                    {sortField === "createdAt" ? (
                      sortOrder === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5 text-blue-600" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                    )}
                  </div>
                </th>

                <th className="px-5 py-3.5 whitespace-nowrap">User</th>
                <th className="px-5 py-3.5 whitespace-nowrap text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-5 py-16 text-center text-sm text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 animate-spin text-black" />
                      <span>Loading real recipe history...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedList.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-5 py-16 text-center text-sm text-gray-500"
                  >
                    <div className="max-w-md mx-auto flex flex-col items-center justify-center gap-2">
                      <Clock className="w-8 h-8 text-gray-300" />
                      <p className="font-semibold text-gray-800">No Recipe History in the Last 90 Days</p>
                      <p className="text-xs text-gray-500">
                        Newly created or saved recipes from the Costing Tool will appear here automatically for fast review, error correction, and remodification.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedList.map((item) => {
                  const isCustom = item.category.toLowerCase().includes("custom");
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50/80 transition-colors group"
                    >
                      {/* SKU - Click to view full details modal */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 font-mono font-bold text-gray-900">
                          <span
                            onClick={() => openDetailModal(item)}
                            className="cursor-pointer hover:text-blue-600 hover:underline transition"
                            title="Click to view full recipe details"
                          >
                            {item.sku}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(item.sku, `sku-${item.id}`);
                            }}
                            className="text-gray-400 hover:text-black hover:bg-gray-100 p-1 rounded-md transition cursor-pointer shrink-0"
                            title="Copy SKU"
                          >
                            {copiedKey === `sku-${item.id}` ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Title - Click to view full details modal */}
                      <td className="px-5 py-3.5 max-w-sm">
                        <div className="flex items-center justify-between gap-1.5">
                          <div
                            onClick={() => openDetailModal(item)}
                            className="font-medium text-gray-800 truncate cursor-pointer hover:text-blue-600 hover:underline transition flex-1"
                            title={`${item.title} (Click to view full recipe details)`}
                          >
                            {item.title}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(item.title, `title-${item.id}`);
                            }}
                            className="text-gray-400 hover:text-black hover:bg-gray-100 p-1 rounded-md transition cursor-pointer shrink-0"
                            title="Copy Title"
                          >
                            {copiedKey === `title-${item.id}` ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                            isCustom
                              ? "bg-purple-50 text-purple-700 border-purple-200"
                              : "bg-blue-50 text-blue-700 border-blue-200"
                          }`}
                        >
                          <Layers className="w-3 h-3" />
                          {item.category}
                        </span>
                      </td>

                      {/* UPC */}
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-700 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Barcode className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span className="font-semibold text-gray-900">{item.upc}</span>
                          {item.upc && item.upc !== "-" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(item.upc, `upc-${item.id}`);
                              }}
                              className="text-gray-400 hover:text-black hover:bg-gray-100 p-1 rounded-md transition cursor-pointer shrink-0"
                              title="Copy UPC"
                            >
                              {copiedKey === `upc-${item.id}` ? (
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Trademark */}
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-md font-medium">
                          <Tag className="w-3 h-3 text-gray-400" />
                          {item.trademark}
                        </span>
                      </td>

                      {/* Date Created */}
                      <td className="px-5 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span>{item.dateCreated}</span>
                        </div>
                      </td>

                      {/* User */}
                      <td className="px-5 py-3.5 text-xs text-gray-700 whitespace-nowrap font-medium">
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span>{item.user}</span>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onSelectRecipe) {
                                onSelectRecipe(item);
                              } else {
                                openRemodifyModal(item);
                              }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black hover:bg-gray-800 text-white rounded-lg text-xs font-bold shadow-xs transition active:scale-95 cursor-pointer"
                            title="Open Full Recipe Builder for this SKU"
                          >
                            <Edit className="w-3.5 h-3.5 text-amber-400" />
                        
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingItem(item);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-xs font-bold transition active:scale-95 cursor-pointer"
                            title="Delete this SKU everywhere"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {sortedList.length > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50/70 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                Showing{" "}
                <strong className="text-gray-900 font-mono">
                  {(currentPage - 1) * itemsPerPage + 1}
                </strong>
                {" - "}
                <strong className="text-gray-900 font-mono">
                  {Math.min(currentPage * itemsPerPage, sortedList.length)}
                </strong>
                {" of "}
                <strong className="text-gray-900 font-mono">{sortedList.length}</strong> recipes
              </span>
              <div className="flex items-center gap-1.5 pl-3 border-l border-gray-300">
                <span className="text-gray-500">Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 text-xs border border-gray-300 rounded-lg bg-white font-semibold text-gray-800 focus:outline-none focus:ring-1 focus:ring-black cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div>
                Page <span className="font-bold text-gray-900 font-mono">{currentPage}</span>{" "}
                of <span className="font-bold text-gray-900 font-mono">{totalPages}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-xl bg-white hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white text-xs font-semibold text-gray-700 transition cursor-pointer shadow-2xs active:scale-95"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-xl bg-white hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white text-xs font-semibold text-gray-700 transition cursor-pointer shadow-2xs active:scale-95"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete Recipe SKU</h3>
                <p className="text-xs text-gray-500">Permanently delete across all collections</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Are you sure you want to permanently delete SKU <strong className="font-mono text-black">{deletingItem.sku}</strong> ({deletingItem.title})?
            </p>
            <div className="bg-red-50 text-red-700 text-xs p-3 rounded-xl border border-red-200">
              ⚠️ This action will permanently remove this record from <strong>New SKU History</strong>, <strong>Price Management</strong>, <strong>Barcode Center</strong>, <strong>RM Database</strong>, and <strong>BOM</strong>.
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setDeletingItem(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => handleDeleteRecipe(deletingItem)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-md transition active:scale-95 cursor-pointer disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Yes, Delete Everywhere
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FULL DETAILS MODAL (Shows ALL recipe formulation, ingredients, packaging, second UOMs, and pricing) */}
      {/* FULL DETAILS MODAL (Shows complete API payload, all barcodes, createdBy, timestamps, ingredients, packaging, 2nd UOMs, and pricing) */}
      {detailModalItem && (() => {
        const createdBy = detailModalSnapshot?.audit?.createdBy || detailModalItem?.user || detailModalItem?.createdBy || detailModalItem?.customFields?.createdBy || "Admin";
        const createdAtVal = detailModalSnapshot?.audit?.createdAt || detailModalItem?.createdAt;
        const updatedAtVal = detailModalSnapshot?.audit?.updatedAt || detailModalItem?.updatedAt;
        const primaryUpc = detailModalSnapshot?.product?.upc || detailModalSnapshot?.audit?.primaryUpc || detailModalItem?.upc;

        // Build list of ALL barcodes associated with this recipe listing
        const allBarcodes = [];
        if (primaryUpc && primaryUpc !== "-" && primaryUpc !== "0") {
          allBarcodes.push({
            label: "Master Product Barcode",
            barcode: primaryUpc,
            sku: detailModalItem.sku,
            type: "UPC-A / EAN-13",
            tag: "Master Unit",
            badgeColor: "bg-amber-100 text-amber-900 border-amber-300"
          });
        }

        const uomRows = detailModalSnapshot?.barcodeSecondUomRows || detailModalItem?.barcodeSecondUom || [];
        if (Array.isArray(uomRows)) {
          uomRows.forEach((row, i) => {
            if (row.eanUpc && row.eanUpc !== "-" && !allBarcodes.some(b => b.barcode === row.eanUpc)) {
              allBarcodes.push({
                label: `Second UOM (${row.secondUom || `Row #${i+1}`})`,
                barcode: row.eanUpc,
                sku: detailModalItem.sku,
                type: row.type || "UPCA",
                description: row.description || `Qty Default: ${row.quantityDefault || 1}`,
                tag: row.secondUom || "2nd UOM",
                badgeColor: "bg-blue-100 text-blue-900 border-blue-300"
              });
            }
          });
        }

        const bulks = detailModalSnapshot?.bulkFormats || [];
        if (Array.isArray(bulks)) {
          bulks.forEach((bf) => {
            if (bf.upc && bf.upc !== "-") {
              allBarcodes.push({
                label: `Bulk Format (${bf.displayWeight || `${bf.netWeightKg} kg`})`,
                barcode: bf.upc,
                sku: bf.generatedSku || "-",
                type: "UPCA",
                description: `Net: ${bf.netWeightKg || ""} kg`,
                tag: "Bulk SKU",
                badgeColor: "bg-purple-100 text-purple-900 border-purple-300"
              });
            }
          });
        }

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-6 overflow-y-auto">
            <div className="w-full max-w-5xl bg-white rounded-3xl border border-gray-200 shadow-2xl overflow-hidden flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150">
              {/* Modal Header */}
              <div className="p-6 bg-gradient-to-r from-gray-950 via-gray-900 to-black text-white shrink-0 border-b border-gray-800">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-mono text-xs font-bold bg-amber-400 text-black px-2.5 py-0.5 rounded-md flex items-center gap-1 shadow-xs">
                        SKU: {detailModalItem.sku}
                      </span>
                      <span className="text-xs font-semibold bg-gray-800 text-gray-200 border border-gray-700 px-2.5 py-0.5 rounded-md flex items-center gap-1.5">
                        <Barcode className="w-3.5 h-3.5 text-amber-400" />
                        UPC: {primaryUpc || "-"}
                      </span>
                      <span className="text-xs font-semibold bg-purple-900/60 text-purple-200 border border-purple-700/60 px-2.5 py-0.5 rounded-md">
                        {detailModalItem.category}
                      </span>
                      <span className="text-xs font-semibold bg-blue-900/60 text-blue-200 border border-blue-700/60 px-2.5 py-0.5 rounded-md">
                        {detailModalItem.trademark}
                      </span>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-emerald-950 text-emerald-300 border border-emerald-800">
                        {detailModalSnapshot?.audit?.status || detailModalItem?.status || "RECIPE_CREATED"}
                      </span>
                    </div>

                    <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white flex items-center gap-2">
                      {detailModalItem.title}
                    </h3>
                    {detailModalSnapshot?.product?.nameFr && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        FR: <span className="text-gray-300 font-medium">{detailModalSnapshot.product.nameFr}</span>
                      </p>
                    )}

                    {/* Audit Information Bar (Created By, Created At, Last Modified At) */}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 pt-3 border-t border-gray-800/80 text-xs">
                      <div className="flex items-center gap-1.5 text-gray-300">
                        <User className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        <span className="text-gray-400">Created By:</span>
                        <strong className="text-white font-semibold">{createdBy}</strong>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-300">
                        <Calendar className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        <span className="text-gray-400">Date Created:</span>
                        <strong className="text-white font-mono">{formatDateTime(createdAtVal)}</strong>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-300">
                        <Clock className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        <span className="text-gray-400">Last Modified:</span>
                        <strong className="text-emerald-300 font-mono">{formatDateTime(updatedAtVal)}</strong>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-400">
                        <Tag className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                        <span>All Barcodes:</span>
                        <strong className="text-amber-400 font-mono font-bold">{allBarcodes.length} Registered</strong>
                      </div>
                    </div>
                  </div>

                  {/* Actions Header */}
                  <div className="flex items-center gap-2.5 self-end sm:self-start shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        const itemToEdit = detailModalItem;
                        setDetailModalItem(null);
                        if (onSelectRecipe) {
                          onSelectRecipe(itemToEdit);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-xl text-xs font-bold transition shadow-md active:scale-95 cursor-pointer"
                      title="Open full recipe in Recipe Builder to edit ingredients, formulation, and costs"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      Edit in Builder
                    </button>
                    <button
                      onClick={() => setDetailModalItem(null)}
                      className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white flex items-center justify-center transition cursor-pointer"
                      title="Close modal"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Subtabs Navigation */}
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-800">
                  <button
                    type="button"
                    onClick={() => setActiveDetailTab("ingredients")}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      activeDetailTab === "ingredients"
                        ? "bg-white text-black shadow-sm"
                        : "bg-gray-800/80 text-gray-300 hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Recipe Formulation
                    {detailModalSnapshot?.ingredients?.length > 0 && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full bg-black/10">
                        {detailModalSnapshot.ingredients.length}
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveDetailTab("barcodes")}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      activeDetailTab === "barcodes"
                        ? "bg-white text-black shadow-sm"
                        : "bg-gray-800/80 text-gray-300 hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <Barcode className="w-3.5 h-3.5" />
                    All Barcodes & 2nd UOMs
                    {allBarcodes.length > 0 && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.2 rounded-full bg-amber-400 text-black font-bold">
                        {allBarcodes.length}
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveDetailTab("packaging")}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      activeDetailTab === "packaging"
                        ? "bg-white text-black shadow-sm"
                        : "bg-gray-800/80 text-gray-300 hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <Package className="w-3.5 h-3.5" />
                    Packaging & Bulk Formats
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveDetailTab("pricing")}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      activeDetailTab === "pricing"
                        ? "bg-white text-black shadow-sm"
                        : "bg-gray-800/80 text-gray-300 hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    Costs & Pricing
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveDetailTab("specs")}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      activeDetailTab === "specs"
                        ? "bg-white text-black shadow-sm"
                        : "bg-gray-800/80 text-gray-300 hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <Sliders className="w-3.5 h-3.5" />
                    Listing Specifications
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-6 flex-1 text-gray-800 bg-gray-50/30">
                {detailLoading ? (
                  <div className="py-20 flex flex-col items-center justify-center gap-3 text-gray-500">
                    <RefreshCw className="w-8 h-8 animate-spin text-black" />
                    <p className="text-sm font-semibold">Loading complete recipe data, barcodes, ingredients, and costs...</p>
                  </div>
                ) : (
                  <>
                    {/* TAB 1: Ingredients & Overview */}
                    {activeDetailTab === "ingredients" && (
                      <div className="space-y-5">
                        {/* Audit & Specification Snapshot Bar */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white p-4 rounded-2xl border border-gray-200 text-xs shadow-xs">
                          <div>
                            <span className="text-gray-400 block font-medium">Product Type</span>
                            <span className="font-bold text-gray-900 capitalize">
                              {detailModalSnapshot?.product?.type || detailModalItem.type || "Regular"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400 block font-medium">Batch Weight & UOM</span>
                            <span className="font-bold text-gray-900 font-mono">
                              {detailModalSnapshot?.product?.baseQuantity || detailModalSnapshot?.product?.masterWeightKg || 25} {detailModalSnapshot?.product?.uom || "kg"}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400 block font-medium">Created By</span>
                            <span className="font-bold text-gray-900 flex items-center gap-1">
                              <User className="w-3 h-3 text-gray-400" />
                              {createdBy}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400 block font-medium">Last Modified</span>
                            <span className="font-bold text-gray-900 font-mono">
                              {formatDateTime(updatedAtVal)}
                            </span>
                          </div>
                        </div>

                        {/* Allergens Notice */}
                        {detailModalSnapshot?.product?.allergensEn ? (
                          <div className="p-3.5 bg-amber-50 border border-amber-300 rounded-2xl text-xs text-amber-900 flex items-start gap-2.5">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <div>
                              <span className="font-bold">Contains Allergens: </span>
                              <span>{detailModalSnapshot.product.allergensEn}</span>
                              {detailModalSnapshot.product.allergensFr && (
                                <span className="text-amber-700 ml-1">
                                  (FR: {detailModalSnapshot.product.allergensFr})
                                </span>
                              )}
                              <span className="ml-2 font-mono text-[11px] bg-amber-200/60 px-1.5 py-0.5 rounded font-semibold text-amber-900">
                                Allergen Premium: +$0.25/kg
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="p-3.5 bg-emerald-50 border border-emerald-300 rounded-2xl text-xs text-emerald-900 flex items-center gap-2.5">
                            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                            <div>
                              <span className="font-bold">No Allergens Detected </span>
                              <span className="text-emerald-700 ml-1">
                                (Allergen Premium: $0.00)
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Ingredients Table */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                              <Boxes className="w-4 h-4 text-gray-500" />
                              Recipe Formulation & Raw Ingredients
                            </h4>
                            <span className="text-xs text-gray-500 font-medium">
                              {detailModalSnapshot?.ingredients?.length || 0} component{(detailModalSnapshot?.ingredients?.length || 0) !== 1 ? "s" : ""}
                            </span>
                          </div>

                          <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="min-w-full text-xs">
                              <thead className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                                <tr>
                                  <th className="px-3.5 py-2.5 text-left w-10">#</th>
                                  <th className="px-3.5 py-2.5 text-left">Ingredient Name</th>
                                  <th className="px-3.5 py-2.5 text-left">SKU</th>
                                  <th className="px-3.5 py-2.5 text-right">Weight</th>
                                  <th className="px-3.5 py-2.5 text-center">Unit</th>
                                  <th className="px-3.5 py-2.5 text-right">Ratio (%)</th>
                                  <th className="px-3.5 py-2.5 text-right">Unit Cost ($/kg)</th>
                                  <th className="px-3.5 py-2.5 text-right">Line Total ($)</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {Array.isArray(detailModalSnapshot?.ingredients) && detailModalSnapshot.ingredients.length > 0 ? (
                                  detailModalSnapshot.ingredients.map((ing, idx) => (
                                    <tr key={ing.id || idx} className="hover:bg-gray-50/60">
                                      <td className="px-3.5 py-2 text-gray-400 font-mono">{idx + 1}</td>
                                      <td className="px-3.5 py-2 font-medium text-gray-900">{ing.name || "-"}</td>
                                      <td className="px-3.5 py-2 font-mono text-gray-600">{ing.sku || "-"}</td>
                                      <td className="px-3.5 py-2 text-right font-mono">{Number(ing.weight || 0).toFixed(4)}</td>
                                      <td className="px-3.5 py-2 text-center text-gray-500">{ing.unit || "kg"}</td>
                                      <td className="px-3.5 py-2 text-right font-bold text-gray-800">{Number(ing.ratio || 0).toFixed(2)}%</td>
                                      <td className="px-3.5 py-2 text-right font-mono text-gray-600">
                                        {canSeeDetailedCosts ? `$${Number(ing.unitCost || 0).toFixed(4)}` : "••••••"}
                                      </td>
                                      <td className="px-3.5 py-2 text-right font-mono font-bold text-gray-900">
                                        {canSeeDetailedCosts ? `$${Number(ing.totalCost || 0).toFixed(4)}` : "••••••"}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="8" className="px-4 py-8 text-center text-gray-400">
                                      No ingredients listed in this snapshot. Click "Edit in Builder" to view/add ingredients.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                              {Array.isArray(detailModalSnapshot?.ingredients) && detailModalSnapshot.ingredients.length > 0 && (
                                <tfoot className="bg-gray-50 font-bold border-t-2 border-gray-200">
                                  <tr>
                                    <td colSpan="3" className="px-3.5 py-2.5 text-right">Totals:</td>
                                    <td className="px-3.5 py-2.5 text-right font-mono">
                                      {detailModalSnapshot.ingredients
                                        .reduce((sum, i) => sum + (Number(i.weight) || 0), 0)
                                        .toFixed(4)}
                                    </td>
                                    <td className="px-3.5 py-2.5 text-center text-gray-500">kg</td>
                                    <td className="px-3.5 py-2.5 text-right font-mono text-blue-700">
                                      {detailModalSnapshot.ingredients
                                        .reduce((sum, i) => sum + (Number(i.ratio) || 0), 0)
                                        .toFixed(2)}%
                                    </td>
                                    <td className="px-3.5 py-2.5 text-right">-</td>
                                    <td className="px-3.5 py-2.5 text-right font-mono text-emerald-700">
                                      ${detailModalSnapshot.ingredients
                                        .reduce((sum, i) => sum + (Number(i.totalCost) || 0), 0)
                                        .toFixed(4)}
                                    </td>
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TAB 2: All Barcodes & Second UOMs */}
                    {activeDetailTab === "barcodes" && (
                      <div className="space-y-6 min-w-0 w-full max-w-full">
                        {/* All Registered Barcodes Grid */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                              <Barcode className="w-4 h-4 text-gray-500" />
                              All Registered Barcodes ({allBarcodes.length})
                            </h4>
                            <span className="text-xs text-gray-500">
                              Every barcode generated & saved for this recipe
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {allBarcodes.map((bc, idx) => (
                              <div
                                key={idx}
                                className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs flex flex-col justify-between gap-3 hover:border-gray-300 transition"
                              >
                                <div>
                                  <div className="flex items-center justify-between gap-2 mb-1.5">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${bc.badgeColor || "bg-gray-100 text-gray-800 border-gray-200"}`}>
                                      {bc.tag}
                                    </span>
                                    <span className="text-[11px] font-mono text-gray-400">
                                      {bc.type}
                                    </span>
                                  </div>
                                  <span className="text-xs font-semibold text-gray-900 block">
                                    {bc.label}
                                  </span>
                                  {bc.description && (
                                    <span className="text-[11px] text-gray-500 block mt-0.5">
                                      {bc.description}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center justify-between gap-2 bg-gray-50 p-2.5 rounded-xl border border-gray-200">
                                  <span className="font-mono text-sm font-black text-gray-900 tracking-wider">
                                    {bc.barcode}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleCopy(bc.barcode, `bc_${idx}`)}
                                    className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-600 transition cursor-pointer"
                                    title="Copy barcode"
                                  >
                                    {copiedKey === `bc_${idx}` ? (
                                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                      <Copy className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Horizontal Second UOMs Table (ERPAG Layout) */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs">
                          <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-1.5">
                            <Tag className="w-4 h-4 text-gray-500" />
                            Horizontal Second UOMs Breakdown (ERPAG Table Layout)
                          </h4>

                          {Array.isArray(uomRows) && uomRows.length > 0 ? (
                            <div className="overflow-x-auto rounded-xl border border-gray-200">
                              <table className="min-w-full text-xs">
                                <thead className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                                  <tr>
                                    <th className="px-4 py-2.5 text-left">EAN / UPC</th>
                                    <th className="px-4 py-2.5 text-center">Second UOM</th>
                                    <th className="px-4 py-2.5 text-right">Quantity Default</th>
                                    <th className="px-4 py-2.5 text-left">Description</th>
                                    <th className="px-4 py-2.5 text-center">Type</th>
                                    <th className="px-4 py-2.5 text-left">Label Printing</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 font-mono">
                                  {uomRows.map((row, idx) => (
                                    <tr key={row.id || idx} className="hover:bg-gray-50/60">
                                      <td className="px-4 py-2.5 font-bold text-gray-900">{row.eanUpc || "-"}</td>
                                      <td className="px-4 py-2.5 text-center">
                                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md font-bold text-[11px] border border-blue-200 font-sans">
                                          {row.secondUom}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2.5 text-right font-bold">{row.quantityDefault}</td>
                                      <td className="px-4 py-2.5 font-sans text-gray-700">{row.description || "-"}</td>
                                      <td className="px-4 py-2.5 text-center text-gray-500">{row.type || "UPCA"}</td>
                                      <td className="px-4 py-2.5 font-sans text-gray-600">{row.labelPrinting || "Per quantity"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-500 text-center">
                              No second UOM rows registered for this recipe.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* TAB 3: Packaging & Bulk Formats */}
                    {activeDetailTab === "packaging" && (
                      <div className="space-y-6 min-w-0 w-full max-w-full">
                        {/* Added Packaging Details */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs">
                          <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-1.5">
                            <Package className="w-4 h-4 text-gray-500" />
                            Master Packaging Variant & Materials
                          </h4>
                          {detailModalSnapshot?.addedPackaging ? (
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-3">
                                <div>
                                  <span className="text-xs text-gray-400 block font-medium">Packaging Variant</span>
                                  <span className="font-bold text-gray-900 text-sm">
                                    {detailModalSnapshot.addedPackaging.name || detailModalSnapshot.addedPackaging.format || "Standard Packaging"}
                                  </span>
                                </div>
                                <div className="flex gap-4 text-xs">
                                  <div>
                                    <span className="text-gray-400 block">Size</span>
                                    <span className="font-bold">{detailModalSnapshot.addedPackaging.size || "-"}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-400 block">TI / HI</span>
                                    <span className="font-bold">{detailModalSnapshot.addedPackaging.ti || "-"} / {detailModalSnapshot.addedPackaging.hi || "-"}</span>
                                  </div>
                                </div>
                              </div>
                              {Array.isArray(detailModalSnapshot.addedPackaging.items) && (
                                <table className="min-w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-500 text-left">
                                      <th className="py-1">Component</th>
                                      <th className="py-1">SKU</th>
                                      <th className="py-1 text-right">Qty</th>
                                      <th className="py-1 text-right">Unit Cost</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detailModalSnapshot.addedPackaging.items.map((pkg, idx) => (
                                      <tr key={idx} className="border-t border-gray-200/60">
                                        <td className="py-1 font-medium">{pkg.name}</td>
                                        <td className="py-1 font-mono text-gray-600">{pkg.sku}</td>
                                        <td className="py-1 text-right font-mono">{pkg.qty}</td>
                                        <td className="py-1 text-right font-mono">{canSeeDetailedCosts ? `$${Number(pkg.unitCost || 0).toFixed(4)}` : "••••••"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ) : (
                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-500 text-center">
                              No primary packaging assigned. Standard bulk packaging applies.
                            </div>
                          )}
                        </div>

                        {/* Bulk Formats */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs">
                          <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-1.5">
                            <Layers className="w-4 h-4 text-gray-500" />
                            Bulk Sizes & Generated Formats
                          </h4>
                          {Array.isArray(detailModalSnapshot?.bulkFormats) && detailModalSnapshot.bulkFormats.length > 0 ? (
                            <div className="overflow-x-auto rounded-xl border border-gray-200">
                              <table className="min-w-full text-xs">
                                <thead className="bg-gray-100 text-gray-700 font-bold">
                                  <tr>
                                    <th className="px-4 py-2.5 text-left">Display Size</th>
                                    <th className="px-4 py-2.5 text-right">Net Weight</th>
                                    <th className="px-4 py-2.5 text-left">Generated Bulk SKU</th>
                                    <th className="px-4 py-2.5 text-left">UPC</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {detailModalSnapshot.bulkFormats.map((fmt, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/60">
                                      <td className="px-4 py-2.5 font-bold text-gray-900">{fmt.displayWeight || `${fmt.netWeightKg} kg`}</td>
                                      <td className="px-4 py-2.5 text-right font-mono">{fmt.netWeightKg} kg</td>
                                      <td className="px-4 py-2.5 font-mono font-bold text-blue-700">{fmt.generatedSku || "-"}</td>
                                      <td className="px-4 py-2.5 font-mono text-gray-600">{fmt.upc || "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-500 text-center">
                              No secondary bulk formats configured.
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* TAB 4: Prices & Margins */}
                    {activeDetailTab === "pricing" && (
                      <div className="space-y-6 min-w-0 w-full max-w-full">
                        {/* Cost Summary Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="p-4 bg-blue-50/80 border border-blue-200 rounded-2xl shadow-xs">
                            <span className="text-[11px] text-blue-700 font-bold block uppercase tracking-wider">Final COGS / CS</span>
                            <span className="text-xl font-black text-blue-950 font-mono">
                              ${Number(detailModalSnapshot?.finalCogsPerCsCad || detailModalSnapshot?.priceManagementData?.finalCogsPerCsCad || detailModalItem.totalCost || 0).toFixed(3)}
                            </span>
                          </div>
                          <div className="p-4 bg-emerald-50/80 border border-emerald-200 rounded-2xl shadow-xs">
                            <span className="text-[11px] text-emerald-700 font-bold block uppercase tracking-wider">Cost / KG (CAD)</span>
                            <span className="text-xl font-black text-emerald-950 font-mono">
                              ${Number(detailModalSnapshot?.costPerKgCad || detailModalSnapshot?.priceManagementData?.costPerKgCad || detailModalItem.baseCost || 0).toFixed(3)}
                            </span>
                          </div>
                          <div className="p-4 bg-purple-50/80 border border-purple-200 rounded-2xl shadow-xs">
                            <span className="text-[11px] text-purple-700 font-bold block uppercase tracking-wider">Cost / LBS (CAD)</span>
                            <span className="text-xl font-black text-purple-950 font-mono">
                              ${Number(detailModalSnapshot?.costPerLbsCad || detailModalSnapshot?.priceManagementData?.costPerLbsCad || 0).toFixed(3)}
                            </span>
                          </div>
                          <div className="p-4 bg-gray-50 border border-gray-200 rounded-2xl shadow-xs">
                            <span className="text-[11px] text-gray-600 font-bold block uppercase tracking-wider">Throughput Cost</span>
                            <span className="text-xl font-black text-gray-900 font-mono">
                              ${Number(detailModalSnapshot?.throughputCost || detailModalSnapshot?.priceManagementData?.throughputCost || 0).toFixed(3)}
                            </span>
                          </div>
                        </div>

                        {/* Pricing Table */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-xs">
                          <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-1.5">
                            <DollarSign className="w-4 h-4 text-gray-500" />
                            Multi-Channel Pricing & Target Margins
                          </h4>
                          <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="min-w-full text-xs">
                              <thead className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                                <tr>
                                  <th className="px-4 py-2.5 text-left">Price Level / Metric</th>
                                  <th className="px-4 py-2.5 text-right">Master Price ($)</th>
                                  <th className="px-4 py-2.5 text-right">Price / KG ($)</th>
                                  <th className="px-4 py-2.5 text-right">Price / LBS ($)</th>
                                  <th className="px-4 py-2.5 text-right">Margin %</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 font-mono">
                                {(
                                  detailModalSnapshot?.pricingDetails ||
                                  detailModalSnapshot?.priceManagementData?.pricingDetails ||
                                  detailModalItem.costSummaryPrices ||
                                  []
                                ).map((row, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50/60">
                                    <td className="px-4 py-2.5 font-sans font-medium text-gray-900">{row.metric}</td>
                                    <td className="px-4 py-2.5 text-right font-bold text-gray-900">${Number(row.masterPrice || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2.5 text-right text-gray-700">${Number(row.pricePerKg || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2.5 text-right text-gray-600">${Number(row.pricePerLbs || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2.5 text-right font-bold text-emerald-700">{Number(row.marginPercentage || 0).toFixed(1)}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TAB 5: Full Listing Specifications & Custom Fields */}
                    {activeDetailTab === "specs" && (
                      <div className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Basic Details Card */}
                          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs space-y-3">
                            <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-1.5">
                              <Tag className="w-4 h-4 text-blue-600" />
                              Product Identification & ERPAG Metadata
                            </h4>
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                              <div>
                                <dt className="text-gray-400 font-medium">SKU</dt>
                                <dd className="font-mono font-bold text-gray-900">{detailModalItem.sku}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Primary Barcode</dt>
                                <dd className="font-mono font-bold text-gray-900">{primaryUpc || "-"}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Category</dt>
                                <dd className="font-semibold text-gray-800">{detailModalItem.category}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Trademark / Brand</dt>
                                <dd className="font-semibold text-gray-800">{detailModalItem.trademark}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Customer / Private Label</dt>
                                <dd className="font-semibold text-gray-800">{detailModalSnapshot?.detailedSku?.customerName || detailModalItem?.customerName || "-"}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Custom Prefix</dt>
                                <dd className="font-mono text-gray-800">{detailModalItem.sku.substring(0, 4)}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Item Type</dt>
                                <dd className="font-semibold text-gray-800">{detailModalSnapshot?.detailedSku?.itemType || "Product"}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Status</dt>
                                <dd className="font-semibold text-emerald-700">{detailModalSnapshot?.audit?.status || detailModalItem?.status || "RECIPE_CREATED"}</dd>
                              </div>
                            </dl>
                          </div>

                          {/* Names & Language Translations */}
                          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs space-y-3">
                            <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-1.5">
                              <FileText className="w-4 h-4 text-purple-600" />
                              Titles, Translations & Descriptions
                            </h4>
                            <div className="space-y-2 text-xs">
                              <div>
                                <span className="text-gray-400 block font-medium">English Name (nameEn)</span>
                                <span className="font-bold text-gray-900">{detailModalSnapshot?.product?.nameEn || detailModalItem.title}</span>
                              </div>
                              <div>
                                <span className="text-gray-400 block font-medium">French Name (nameFr)</span>
                                <span className="font-bold text-gray-900">{detailModalSnapshot?.product?.nameFr || detailModalSnapshot?.detailedSku?.customFields?.fr || "-"}</span>
                              </div>
                              <div>
                                <span className="text-gray-400 block font-medium">Description EN</span>
                                <span className="text-gray-700">{detailModalSnapshot?.product?.descriptionEn || detailModalSnapshot?.detailedSku?.description || "-"}</span>
                              </div>
                              <div>
                                <span className="text-gray-400 block font-medium">Description FR</span>
                                <span className="text-gray-700">{detailModalSnapshot?.product?.descriptionFr || detailModalSnapshot?.detailedSku?.descriptionFr || "-"}</span>
                              </div>
                            </div>
                          </div>

                          {/* Weights, Units & Logistics */}
                          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs space-y-3">
                            <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-1.5">
                              <Boxes className="w-4 h-4 text-amber-600" />
                              Units, Weights & Logistics
                            </h4>
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                              <div>
                                <dt className="text-gray-400 font-medium">Master Batch Weight</dt>
                                <dd className="font-mono font-bold text-gray-900">{detailModalSnapshot?.product?.masterWeightKg || 25} kg</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Base UOM</dt>
                                <dd className="font-mono text-gray-900">{detailModalSnapshot?.product?.uom || detailModalSnapshot?.detailedSku?.uom || "kg"}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Warehouse Description</dt>
                                <dd className="text-gray-800">{detailModalSnapshot?.detailedSku?.warehouseDescription || "Main warehouse"}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Inventory Quantity</dt>
                                <dd className="font-mono text-gray-800">{detailModalSnapshot?.detailedSku?.inventoryQuantity || 0}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Country of Origin</dt>
                                <dd className="text-gray-800">{detailModalSnapshot?.detailedSku?.countryOfOrigin || "Canada (CA)"}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Harmonized Tariff (HS/FDA)</dt>
                                <dd className="font-mono text-gray-800">{detailModalSnapshot?.detailedSku?.customFields?.fdaCode || detailModalSnapshot?.detailedSku?.harmonizedTariff || "-"}</dd>
                              </div>
                            </dl>
                          </div>

                          {/* Audit & Database Storage */}
                          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-xs space-y-3">
                            <h4 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2 flex items-center gap-1.5">
                              <Clock className="w-4 h-4 text-emerald-600" />
                              Audit Trail & Creation Records
                            </h4>
                            <dl className="grid grid-cols-1 gap-y-2 text-xs">
                              <div>
                                <dt className="text-gray-400 font-medium">Author / Created By</dt>
                                <dd className="font-bold text-gray-900">{createdBy}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Created Timestamp (createdAt)</dt>
                                <dd className="font-mono text-gray-900">{formatDateTime(createdAtVal)}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Last Modified Timestamp (updatedAt)</dt>
                                <dd className="font-mono font-bold text-emerald-700">{formatDateTime(updatedAtVal)}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400 font-medium">Database Document ID (_id)</dt>
                                <dd className="font-mono text-gray-500 text-[11px] truncate">{detailModalItem.id || detailModalSnapshot?.detailedSku?._id || "-"}</dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                <div className="text-xs text-gray-500 flex items-center gap-3">
                  <span>Recipe SKU: <strong className="font-mono text-gray-800">{detailModalItem.sku}</strong></span>
                  <span>|</span>
                  <span>Reserved UPC: <strong className="font-mono text-gray-800">{primaryUpc || "-"}</strong></span>
                  <span>|</span>
                  <span>Author: <strong className="text-gray-800">{createdBy}</strong></span>
                </div>
                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setDetailModalItem(null)}
                    className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-white transition cursor-pointer"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSyncToErpag(detailModalItem?.sku)}
                    disabled={syncingSku === detailModalItem?.sku}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1e40af] hover:bg-[#1d4ed8] text-white rounded-xl text-xs font-bold shadow-md transition active:scale-95 cursor-pointer disabled:opacity-50"
                  >
                    {syncingSku === detailModalItem?.sku ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                        Sync to ERPAG
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const itemToEdit = detailModalItem;
                      setDetailModalItem(null);
                      if (onSelectRecipe) {
                        onSelectRecipe(itemToEdit);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-5 py-2 bg-black hover:bg-gray-800 text-white rounded-xl text-xs font-bold shadow-md transition active:scale-95 cursor-pointer"
                  >
                    <Edit className="w-3.5 h-3.5 text-amber-400" />
                    Edit This Recipe in Builder
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
